<script setup>
import { usePlatformApi } from '@/utils/platformApi'

// Addendum 15, Modul BR. Sama pola dua-lapis keamanan dengan halaman
// /platform lain -- lihat komentar di platform/tenants/index.vue.
definePage({ meta: { layout: 'platform', public: true } })

const router = useRouter()

const platformAccessToken = useCookie('platformAccessToken')
const platformAdminData = useCookie('platformAdminData')

if (!platformAccessToken.value)
  await router.push('/platform/login')

const posts = ref([])
const loading = ref(false)
const statusFilter = ref(null)
const page = ref(1)
const perPage = ref(20)
const totalItems = ref(0)
const actionError = ref('')

const statusOptions = [
  { title: 'Semua Status', value: null },
  { title: 'Draft', value: 'draft' },
  { title: 'Published', value: 'published' },
]

const headers = [
  { title: 'Judul', key: 'title', sortable: false },
  { title: 'Slug', key: 'slug', sortable: false },
  { title: 'Status', key: 'status', sortable: false },
  { title: 'Tanggal', key: 'date', sortable: false },
  { title: 'Aksi', key: 'actions', sortable: false, align: 'center', width: 160 },
]

const handleAuthError = async err => {
  const status = err?.status ?? err?.response?.status
  if (status === 401 || status === 403) {
    platformAccessToken.value = null
    platformAdminData.value = null
    await router.push('/platform/login')

    return true
  }

  return false
}

const fetchPosts = async () => {
  loading.value = true
  try {
    const query = { page: page.value, per_page: perPage.value }
    if (statusFilter.value)
      query.status = statusFilter.value

    const res = await usePlatformApi('/api/v1/platform/blog-posts', { query }).get()
    const body = res?.data ?? res

    posts.value = body?.data ?? []
    totalItems.value = body?.pagination?.total ?? 0
  } catch (err) {
    if (await handleAuthError(err))
      return

    console.error('[Platform] fetch blog-posts error', err)
    posts.value = []
  } finally {
    loading.value = false
  }
}

watch([statusFilter, page], fetchPosts, { immediate: true })

const togglingId = ref(null)

const togglePublish = async post => {
  togglingId.value = post.id
  actionError.value = ''
  try {
    const action = post.status === 'published' ? 'unpublish' : 'publish'

    await usePlatformApi(`/api/v1/platform/blog-posts/${post.id}/${action}`).post()
    fetchPosts()
  } catch (err) {
    if (await handleAuthError(err))
      return

    const data = err?.data ?? err?.response?._data

    actionError.value = data?.message ?? 'Gagal mengubah status artikel.'
  } finally {
    togglingId.value = null
  }
}

// ─── Delete ───────────────────────────────────────────────────────────────
const deleteDialog = ref(false)
const selectedPost = ref(null)
const deleteLoading = ref(false)
const deleteError = ref('')

const confirmDelete = post => {
  selectedPost.value = post
  deleteError.value = ''
  deleteDialog.value = true
}

const doDelete = async () => {
  deleteLoading.value = true
  deleteError.value = ''
  try {
    await usePlatformApi(`/api/v1/platform/blog-posts/${selectedPost.value.id}`).delete()
    deleteDialog.value = false
    fetchPosts()
  } catch (err) {
    if (await handleAuthError(err))
      return

    const data = err?.data ?? err?.response?._data

    deleteError.value = data?.message ?? 'Gagal menghapus artikel.'
  } finally {
    deleteLoading.value = false
  }
}
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center justify-space-between mb-4 flex-wrap gap-3">
      <div>
        <h4 class="text-h4">
          Blog
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Login sebagai {{ platformAdminData?.name }}
        </p>
      </div>

      <VBtn
        color="primary"
        :to="{ path: '/platform/blog-posts/create' }"
      >
        Tulis Artikel
      </VBtn>
    </div>

    <VAlert
      v-if="actionError"
      type="error"
      variant="tonal"
      class="mb-4"
      closable
      @click:close="actionError = ''"
    >
      {{ actionError }}
    </VAlert>

    <VCard>
      <VCardText class="d-flex align-center">
        <VSelect
          v-model="statusFilter"
          :items="statusOptions"
          label="Filter status"
          density="compact"
          style="max-width: 240px;"
        />
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:page="page"
        :headers="headers"
        :items="posts"
        :items-length="totalItems"
        :loading="loading"
        :items-per-page="perPage"
        no-data-text="Belum ada artikel."
        @update:options="fetchPosts"
      >
        <template #item.slug="{ item }">
          <code>/blog/{{ item.slug }}</code>
        </template>

        <template #item.status="{ item }">
          <VChip
            :color="item.status === 'published' ? 'success' : 'secondary'"
            size="small"
          >
            {{ item.status === 'published' ? 'Published' : 'Draft' }}
          </VChip>
        </template>

        <template #item.date="{ item }">
          {{
            item.published_at
              ? new Date(item.published_at).toLocaleDateString('id-ID')
              : `Dibuat ${new Date(item.created_at).toLocaleDateString('id-ID')}`
          }}
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex gap-1 justify-center">
            <IconBtn
              :color="item.status === 'published' ? 'warning' : 'success'"
              :loading="togglingId === item.id"
              @click="togglePublish(item)"
            >
              <VIcon :icon="item.status === 'published' ? 'ri-eye-off-line' : 'ri-eye-line'" />
              <VTooltip activator="parent">
                {{ item.status === 'published' ? 'Tarik ke Draft' : 'Publish' }}
              </VTooltip>
            </IconBtn>
            <IconBtn :to="{ path: `/platform/blog-posts/${item.id}/edit` }">
              <VIcon icon="ri-pencil-line" />
              <VTooltip activator="parent">
                Edit
              </VTooltip>
            </IconBtn>
            <IconBtn
              color="error"
              @click="confirmDelete(item)"
            >
              <VIcon icon="ri-delete-bin-line" />
              <VTooltip activator="parent">
                Hapus
              </VTooltip>
            </IconBtn>
          </div>
        </template>
      </VDataTableServer>
    </VCard>
  </div>

  <VDialog
    v-model="deleteDialog"
    max-width="440"
    persistent
  >
    <VCard>
      <VCardTitle class="pt-5 px-5">
        Hapus Artikel?
      </VCardTitle>
      <VCardText class="px-5">
        Yakin ingin menghapus <strong>{{ selectedPost?.title }}</strong>? Tindakan ini permanen dan
        tidak bisa dibatalkan.

        <VAlert
          v-if="deleteError"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          {{ deleteError }}
        </VAlert>
      </VCardText>
      <VCardActions class="px-5 pb-5">
        <VSpacer />
        <VBtn
          variant="tonal"
          @click="deleteDialog = false"
        >
          Batal
        </VBtn>
        <VBtn
          color="error"
          :loading="deleteLoading"
          @click="doDelete"
        >
          Hapus
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
